import React from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Link,
  Typography,
  Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getSubMenuGroup, getSubMenuGroups } from "./sub-menu-utils";
import { mediaUrl } from "../../../../utils/cfImage";

const dropdownNavLinkSx = {
  fontFamily: "Spartan-SemiBold",
  color: "#000",
  fontSize: 13,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  textDecoration: "none",
  display: "inline-block",
  width: "fit-content",
  borderBottom: "1px solid transparent",
  transition: "color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    color: "rgba(0,0,0,0.5)",
    borderBottomColor: "rgba(0,0,0,0.5)",
  },
};

function DropdownNavLink({ to, onClick, children }) {
  return (
    <Link
      component={RouterLink}
      to={to}
      onClick={onClick}
      sx={dropdownNavLinkSx}
    >
      {children}
    </Link>
  );
}

// Reusable media card with hover zoom and bottom CTA overlay.
export function PromoImageCard({
  imageUrl,
  imageAlt,
  imageLink,
  ctaText,
  ctaLink,
  onNavigate,
}) {
  return (
    <Box
      sx={{
        textAlign: "center",
        "&:hover": {
          "& > div > a > img": {
            transform: "scale(1.3)",
            webkitTransform: "scale(1.3)",
          },
        },
      }}
    >
      <Box
        className="box-img-zoomed"
        sx={{ overflow: "hidden", position: "relative" }}
      >
        <Link
          component={RouterLink}
          to={imageLink || "#"}
          onClick={onNavigate}
          sx={{
            img: {
              width: "100%",
              height: "auto",
              transition: "transform .5s ease",
              webkitTransition: "transform .5s ease",
            },
          }}
        >
          <img src={imageUrl} alt={imageAlt} />
        </Link>
        {ctaText && (
          <Stack
            spacing={0.5}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              pl: { xs: 1.5, md: 2.5 },
              pb: { xs: 1, md: 1.5 },
              alignItems: "flex-start",
            }}
          >
            <Button
              component={RouterLink}
              to={ctaLink || imageLink || "#"}
              sx={{
                color: "#fff",
                fontSize: { xs: 8, sm: 11, md: 13 },
                borderBottom: "1px solid #fff",
                borderRadius: 0,
                fontWeight: 600,
                lineHeight: 1,
                p: 0,
                minWidth: 0,
                fontFamily: "Spartan-ExtraBold",
                "&:hover,&:focus": {
                  backgroundColor: "transparent",
                  borderBottom: "1px solid transparent",
                },
              }}
              disableRipple
            >
              {ctaText}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

// Desktop dropdown panels and footer utility links.
export default function DesktopDropdownContent(props) {
  const {
    menu,
    handleToggle,
    devHelper,
    womens,
    mens,
    gifts,
    collections,
    filesUrl,
    imgVersion,
  } = props;

  return (
    <Box sx={{ position: "relative", display: { xs: "none", lg: "block" } }}>
      <Box
        onMouseEnter={() => {
          handleToggle(true);
        }}
        onMouseLeave={() => {
          handleToggle(false);
        }}
        sx={{
          m: 0,
          position: "absolute",
          left: 0,
          zIndex: 3000,
          width: "100%",
          backgroundColor: "#fff",
        }}
      >
        <Container maxWidth="xl">
          {menu === "womens" &&
            (() => {
              const category = getSubMenuGroup(
                devHelper.getObjectValue(womens, "subMenu.category", {}),
              );
              const material = getSubMenuGroup(
                devHelper.getObjectValue(womens, "subMenu.material", {}),
              );
              const collection = getSubMenuGroup(
                devHelper.getObjectValue(womens, "subMenu.collection", {}),
              );
              const featured = getSubMenuGroup(
                devHelper.getObjectValue(womens, "subMenu.featured", {}),
              );

              return (
                <Grid
                  container
                  direction="row"
                  spacing={3}
                  sx={{ my: 2, pt: 2, pb: 4 }}
                >
                  <Grid item xs={6}>
                    <Grid container direction="row" spacing={1} rowSpacing={4}>
                      <Grid item xs={12} lg={4}>
                        <Typography
                          sx={{
                            fontFamily: "GillSans",
                            color: "#000",
                            fontSize: 22,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          {category.title}
                        </Typography>
                        <Stack spacing={2}>
                          {category.items.map((val) => {
                            const { slug, title = "", url } = val;
                            return (
                              <DropdownNavLink
                                key={slug}
                                to={url}
                                onClick={() => handleToggle(false)}
                              >
                                {title}
                              </DropdownNavLink>
                            );
                          })}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Stack spacing={5}>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "GillSans",
                                color: "#000",
                                fontSize: 22,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                mb: 3,
                              }}
                            >
                              {material.title}
                            </Typography>
                            <Stack spacing={2}>
                              {material.items.map((val) => {
                                const { slug, title = "", url } = val;
                                return (
                                  <DropdownNavLink
                                    key={slug}
                                    to={url}
                                    onClick={() => handleToggle(false)}
                                  >
                                    {title}
                                  </DropdownNavLink>
                                );
                              })}
                            </Stack>
                          </Stack>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "GillSans",
                                color: "#000",
                                fontSize: 22,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                mb: 3,
                              }}
                            >
                              {collection.title}
                            </Typography>
                            <Stack spacing={2}>
                              {collection.items.map((val) => {
                                const { slug, title = "", url } = val;
                                return (
                                  <DropdownNavLink
                                    key={slug}
                                    to={url}
                                    onClick={() => handleToggle(false)}
                                  >
                                    {title}
                                  </DropdownNavLink>
                                );
                              })}
                            </Stack>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Typography
                          sx={{
                            fontFamily: "GillSans",
                            color: "#000",
                            fontSize: 22,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          {featured.title}
                        </Typography>
                        <Stack spacing={2}>
                          {featured.items.map((val) => {
                            const { slug, title = "", url } = val;
                            return (
                              <DropdownNavLink
                                key={slug}
                                to={url}
                                onClick={() => handleToggle(false)}
                              >
                                {title}
                              </DropdownNavLink>
                            );
                          })}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container direction="row" spacing={2}>
                      <Grid item xs={6}>
                        <PromoImageCard
                          imageUrl={mediaUrl(
                            filesUrl,
                            "marketing/menu/menu-07-2026/theodora-collection.jpg",
                            { width: 1000, height: 1000, version: imgVersion },
                          )}
                          imageAlt="THEODORA COLLECTION"
                          imageLink="/women/collection/theodora"
                          ctaText="theodora collection"
                          ctaLink="/women/collection/theodora"
                          onNavigate={() => handleToggle(false)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <PromoImageCard
                          imageUrl={mediaUrl(
                            filesUrl,
                            "marketing/menu/menu-07-2026/neda-collection.jpg",
                            { width: 1000, height: 1000, version: imgVersion },
                          )}
                          imageAlt="NEDA COLLECTION"
                          imageLink="/women/collection/neda"
                          ctaText="neda collection"
                          ctaLink="/women/collection/neda"
                          onNavigate={() => handleToggle(false)}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })()}

          {menu === "mens" &&
            (() => {
              const category = getSubMenuGroup(
                devHelper.getObjectValue(mens, "subMenu.category", {}),
              );
              const material = getSubMenuGroup(
                devHelper.getObjectValue(mens, "subMenu.material", {}),
              );
              const collection = getSubMenuGroup(
                devHelper.getObjectValue(mens, "subMenu.collection", {}),
              );
              const featured = getSubMenuGroup(
                devHelper.getObjectValue(mens, "subMenu.featured", {}),
              );

              return (
                <Grid
                  container
                  direction="row"
                  spacing={3}
                  sx={{ my: 2, pt: 2, pb: 4 }}
                >
                  <Grid item xs={6}>
                    <Grid container direction="row" spacing={1} rowSpacing={4}>
                      <Grid item xs={12} lg={4}>
                        <Typography
                          sx={{
                            fontFamily: "GillSans",
                            color: "#000",
                            fontSize: 22,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          {category.title}
                        </Typography>
                        <Stack spacing={2}>
                          {category.items.map((val) => {
                            const { slug, title = "", url } = val;
                            return (
                              <DropdownNavLink
                                key={slug}
                                to={url}
                                onClick={() => handleToggle(false)}
                              >
                                {title}
                              </DropdownNavLink>
                            );
                          })}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Stack spacing={5}>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "GillSans",
                                color: "#000",
                                fontSize: 22,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                mb: 3,
                              }}
                            >
                              {material.title}
                            </Typography>
                            <Stack spacing={2}>
                              {material.items.map((val) => {
                                const { slug, title = "", url } = val;
                                return (
                                  <DropdownNavLink
                                    key={slug}
                                    to={url}
                                    onClick={() => handleToggle(false)}
                                  >
                                    {title}
                                  </DropdownNavLink>
                                );
                              })}
                            </Stack>
                          </Stack>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "GillSans",
                                color: "#000",
                                fontSize: 22,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                mb: 3,
                              }}
                            >
                              {collection.title}
                            </Typography>
                            <Stack spacing={2}>
                              {collection.items.map((val) => {
                                const { slug, title = "", url } = val;
                                return (
                                  <DropdownNavLink
                                    key={slug}
                                    to={url}
                                    onClick={() => handleToggle(false)}
                                  >
                                    {title}
                                  </DropdownNavLink>
                                );
                              })}
                            </Stack>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Typography
                          sx={{
                            fontFamily: "GillSans",
                            color: "#000",
                            fontSize: 22,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          {featured.title}
                        </Typography>
                        <Stack spacing={2}>
                          {featured.items.map((val) => {
                            const { slug, title = "", url } = val;
                            return (
                              <DropdownNavLink
                                key={slug}
                                to={url}
                                onClick={() => handleToggle(false)}
                              >
                                {title}
                              </DropdownNavLink>
                            );
                          })}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container direction="row" spacing={2}>
                      <Grid item xs={6}>
                        <PromoImageCard
                          imageUrl={mediaUrl(
                            filesUrl,
                            "marketing/menu/menu-07-2026/kavafi-collection.jpg",
                            { width: 1000, height: 1000, version: imgVersion },
                          )}
                          imageAlt="KAVAFI COLLECTION"
                          imageLink="/men/collection/kavafi"
                          ctaText="kavafi collection"
                          ctaLink="/men/collection/kavafi"
                          onNavigate={() => handleToggle(false)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <PromoImageCard
                          imageUrl={mediaUrl(
                            filesUrl,
                            "marketing/menu/menu-07-2026/kavos-collection.jpg",
                            { width: 1000, height: 1000, version: imgVersion },
                          )}
                          imageAlt="KAVOS COLLECTION"
                          imageLink="/men/collection/kavos"
                          ctaText="kavos collection"
                          ctaLink="/men/collection/kavos"
                          onNavigate={() => handleToggle(false)}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })()}

          {menu === "gifts" &&
            (() => {
              const shopGifts = getSubMenuGroup(
                devHelper.getObjectValue(gifts, "subMenu.shop-gifts", {}),
              );
              const giftsByPriceHer = getSubMenuGroup(
                devHelper.getObjectValue(
                  gifts,
                  "subMenu.gifts-by-price-her",
                  {},
                ),
              );
              const giftsByPriceHim = getSubMenuGroup(
                devHelper.getObjectValue(
                  gifts,
                  "subMenu.gifts-by-price-him",
                  {},
                ),
              );
              const services = getSubMenuGroup(
                devHelper.getObjectValue(gifts, "subMenu.services", {}),
              );

              return (
                <Grid
                  container
                  direction="row"
                  spacing={3}
                  sx={{ my: 2, pt: 2, pb: 4 }}
                >
                  <Grid item xs={6}>
                    <Grid container direction="row" spacing={1} rowSpacing={4}>
                      <Grid item xs={12} lg={3}>
                        <Typography
                          sx={{
                            fontFamily: "GillSans",
                            color: "#000",
                            fontSize: 22,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          {shopGifts.title}
                        </Typography>
                        <Stack spacing={2}>
                          {shopGifts.items.map((val) => {
                            const { slug, title = "", url } = val;
                            return (
                              <DropdownNavLink
                                key={slug}
                                to={url}
                                onClick={() => handleToggle(false)}
                              >
                                {title}
                              </DropdownNavLink>
                            );
                          })}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={4.5}>
                        <Typography
                          sx={{
                            fontFamily: "GillSans",
                            color: "#000",
                            fontSize: 22,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          {giftsByPriceHer.title}
                        </Typography>
                        <Stack spacing={2}>
                          {giftsByPriceHer.items.map((val) => {
                            const { slug, title = "", url } = val;
                            return (
                              <DropdownNavLink
                                key={slug}
                                to={url}
                                onClick={() => handleToggle(false)}
                              >
                                {title}
                              </DropdownNavLink>
                            );
                          })}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={4.5}>
                        <Stack spacing={5}>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "GillSans",
                                color: "#000",
                                fontSize: 22,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                mb: 3,
                              }}
                            >
                              {giftsByPriceHim.title}
                            </Typography>
                            <Stack spacing={2}>
                              {giftsByPriceHim.items.map((val) => {
                                const { slug, title = "", url } = val;
                                return (
                                  <DropdownNavLink
                                    key={slug}
                                    to={url}
                                    onClick={() => handleToggle(false)}
                                  >
                                    {title}
                                  </DropdownNavLink>
                                );
                              })}
                            </Stack>
                          </Stack>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "GillSans",
                                color: "#000",
                                fontSize: 22,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                mb: 3,
                              }}
                            >
                              {services.title}
                            </Typography>
                            <Stack spacing={2}>
                              {services.items.map((val) => {
                                const { slug, title = "", url } = val;
                                return (
                                  <DropdownNavLink
                                    key={slug}
                                    to={url}
                                    onClick={() => handleToggle(false)}
                                  >
                                    {title}
                                  </DropdownNavLink>
                                );
                              })}
                            </Stack>
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container direction="row" spacing={2}>
                      <Grid item xs={6}>
                        <PromoImageCard
                          imageUrl={mediaUrl(
                            filesUrl,
                            "marketing/menu/menu-07-2026/gifts-1.jpg",
                            { width: 1000, height: 1000, version: imgVersion },
                          )}
                          imageAlt="GIFTS 1"
                          imageLink="/gifts"
                          // ctaText="Shop All"
                          ctaLink="/gifts"
                          onNavigate={() => handleToggle(false)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <PromoImageCard
                          imageUrl={mediaUrl(
                            filesUrl,
                            "marketing/menu/menu-07-2026/gifts-2.jpg",
                            { width: 1000, height: 1000, version: imgVersion },
                          )}
                          imageAlt="GIFTS 2"
                          imageLink="/gifts"
                          // ctaText="Shop All"
                          ctaLink="/gifts"
                          onNavigate={() => handleToggle(false)}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })()}

          {menu === "collections" && (
            <Grid
              container
              direction="row"
              spacing={3}
              sx={{ my: 2, pt: 2, pb: 4 }}
            >
              <Grid item xs={6}>
                <Grid container direction="row" spacing={1} rowSpacing={4}>
                  {getSubMenuGroups(
                    devHelper.getObjectValue(collections, "subMenu", {}),
                  ).map(({ key, title, items }) => (
                    <Grid item xs={6} key={key}>
                      <Typography
                        sx={{
                          fontFamily: "GillSans",
                          color: "#000",
                          fontSize: 22,
                          textTransform: "uppercase",
                          fontWeight: 600,
                          mb: 3,
                        }}
                      >
                        {title}
                      </Typography>
                      <Stack spacing={2}>
                        {items.map((val) => {
                          const { slug, title: linkTitle = "", url } = val;
                          return (
                            <DropdownNavLink
                              key={`${key}-${slug}`}
                              to={url}
                              onClick={() => handleToggle(false)}
                            >
                              {linkTitle}
                            </DropdownNavLink>
                          );
                        })}
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12}>
                    <PromoImageCard
                      imageUrl={mediaUrl(
                        filesUrl,
                        "marketing/menu/menu-07-2026/collections.jpg",
                        { width: 1000, version: imgVersion },
                      )}
                      imageAlt="WOMEN'S COLLECTION"
                      imageLink="/women/collections"
                      // ctaText="women's collections"
                      // ctaLink="/women/collections"
                      onNavigate={() => handleToggle(false)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Container>

        <Box
          sx={{
            bgcolor: "#b6b7ae",
            py: 2,
          }}
        >
          <Container maxWidth="xl">
            <Stack
              direction="row"
              spacing={6}
              sx={{
                mt: 0.5,
                justifyContent: "flex-end",
                "& a": {
                  color: "#fff",
                  fontSize: "14px",
                  borderBottom: "1px solid transparent",
                  borderRadius: 0,
                  fontWeight: 600,
                  lineHeight: 1,
                  p: 0,
                  fontFamily: "Spartan-Regular",
                  "&:hover,&:focus": {
                    backgroundColor: "transparent",
                    borderBottom: "1px solid #fff",
                  },
                },
              }}
            >
              <Button to="/contact-us" component={RouterLink} disableRipple>
                Contact Us
              </Button>
              <Button to="/about" component={RouterLink} disableRipple>
                K-World
              </Button>
              <Button to="/boutiques" component={RouterLink} disableRipple>
                Store Locator
              </Button>
              <Button to="/trunk-shows" component={RouterLink} disableRipple>
                Announcements
              </Button>
            </Stack>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
